import { useMutation } from '@apollo/client';
import { ErrorBoundary, QueryString, OptionalString } from '@sixfold/app-utils';
import { AlertModal, Button, Icon, Paragraph, Stack, useTheme } from '@sixfold/common-ui';
import React, { useCallback, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useLocation } from 'react-router';

import { ErrorPlaceholder } from '../../components/error_placeholder';
import { isValid } from '../../lib/date';
import { MockTourVehicleMutation, MockTourVehicleMutationVariables } from '../../lib/graphql';
import { MapSlider, TIMELINE_TIMESTAMP_QS } from '../../map/components/map_slider';
import { VehicleHistory, VehicleBreakHistory, Vehicle, VehicleStatus } from '../../vehicle/entities';
import { isFMSTracker } from '../../vehicle/utils';
import { TimelineModal } from '../components/timeline';
import { TourMapView } from '../components/tour_map';
import { Tour } from '../entities';
import { mockTourVehicleMutation } from '../graphql';
import { getStopsFromTourStops, getExternalEvents } from '../utils';

export interface Props {
  tour: Tour;
  vehicles: (Vehicle & {
    history: VehicleHistory[];
    breakHistory: VehicleBreakHistory[];
    status: VehicleStatus | null;
  })[];
  tourSidebarState: {
    tourSidebarActive: boolean;
    setTourSidebarActive: React.Dispatch<React.SetStateAction<boolean>>;
  };
  historyFetchingBoundaries: {
    startTime: string;
    endTime: string;
  };
}

export const TourMapContainer = ({ tour, vehicles, historyFetchingBoundaries, tourSidebarState }: Props) => {
  const location = useLocation();
  const { currentTheme } = useTheme();
  const { startTime, endTime } = historyFetchingBoundaries;
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [mockTourPopup, setMockTourPopup] = useState<
    { kind: 'started' } | { kind: 'notUnallocated' } | { kind: 'failed'; message: string } | null
  >(null);
  const [mockTourVehicle, { loading: mockTourLoading }] = useMutation<
    MockTourVehicleMutation,
    MockTourVehicleMutationVariables
  >(mockTourVehicleMutation);

  const csvInput = vehicles
    .map((vehicle) =>
      vehicle.history.map((history) => {
        return {
          vehicle_id: vehicle.vehicle_id,
          plate: vehicle.license_plate_number,
          source: history.source,
          type: history.type,
          company_id: history.company?.company_id,
          timestamp: history.timestamp,
          latitude: history.lat,
          longitude: history.lng,
          ...(isFMSTracker(history.vehicleTracker)
            ? {
                sixfold_received_position_at: history.received_at ?? 'unknown',
                sixfold_processesed_position_at: history.created_at ?? 'unknown',
                telematics_provider_name:
                  history.vehicleTracker.telematicsIntegration?.telematicsProvider?.telematicsProviderName ?? 'unknown',
                telematics_integration_id:
                  history.vehicleTracker.telematicsIntegration?.telematicsIntegrationId ?? 'unknown',
              }
            : {}),
        };
      }),
    )
    .flat();

  const toggleTourSidebar = useCallback(() => {
    tourSidebarState.setTourSidebarActive(!tourSidebarState.tourSidebarActive);
  }, [tourSidebarState]);

  const openTimelineModal = useCallback(() => {
    setTimelineModalOpen(true);
  }, []);

  const closeTimelineModal = useCallback(() => {
    setTimelineModalOpen(false);
  }, []);

  const startMockTour = useCallback(async () => {
    if (tour.status !== 'unallocated') {
      setMockTourPopup({ kind: 'notUnallocated' });
      return;
    }
    try {
      await mockTourVehicle({ variables: { tour_id: tour.tour_id } });
      setMockTourPopup({ kind: 'started' });
    } catch (error) {
      setMockTourPopup({ kind: 'failed', message: error instanceof Error ? error.message : 'Unknown error' });
    }
  }, [mockTourVehicle, tour.status, tour.tour_id]);

  const closeMockTourPopup = useCallback(() => {
    setMockTourPopup(null);
  }, []);

  return (
    <div className="tour__map-column">
      <Stack direction="row" gap="small" alignItems="center" flexWrap="wrap">
        <CSVLink filename={`tour_${tour.tour_id}_vehicles_history.csv`} data={csvInput}>
          <Button size="medium" kind="subtle">
            Download tour vehicles history
          </Button>
        </CSVLink>
        <Button size="medium" kind="subtle" onClick={toggleTourSidebar}>
          {tourSidebarState.tourSidebarActive ? 'Hide' : 'Show'} tour sidebar
        </Button>
        <Button
          size="medium"
          onClick={openTimelineModal}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            boxShadow: '0 2px 4px rgba(102, 126, 234, 0.4)',
          }}>
          <Stack direction="row" gap="xsmall" alignItems="center">
            <Icon name="small-play" color="#4ade80" />
            Timeline
          </Stack>
        </Button>
        <Button size="medium" kind="subtle" onClick={startMockTour} disabled={mockTourLoading}>
          Mock tour
        </Button>
      </Stack>
      <AlertModal
        open={mockTourPopup !== null}
        onClose={closeMockTourPopup}
        trigger={null}
        title="Mock tour"
        description={
          mockTourPopup?.kind === 'started' ? (
            <Paragraph>
              <Stack direction="row" gap="xsmall" alignItems="center">
                <Icon name="small-check" kind="success" />
                Tour mocking successfully started
              </Stack>
            </Paragraph>
          ) : (
            <Paragraph>
              <Stack direction="row" gap="xsmall" alignItems="center">
                <Icon name="small-cross" kind="danger" />
                {mockTourPopup?.kind === 'failed'
                  ? `Failed to start tour mocking: ${mockTourPopup.message}`
                  : 'Mocking can only start for unallocated tours'}
              </Stack>
            </Paragraph>
          )
        }>
        <Button kind="primary" onClick={closeMockTourPopup}>
          OK
        </Button>
      </AlertModal>
      <TimelineModal
        tour={tour}
        vehicles={vehicles.map((v) => ({
          vehicleId: v.vehicle_id,
          licensePlateNumber: v.license_plate_number,
          history: v.history,
          breakHistory: v.breakHistory,
        }))}
        open={timelineModalOpen}
        onClose={closeTimelineModal}
      />
      <ErrorBoundary renderError={(error) => <ErrorPlaceholder error={error} />}>
        <QueryString queryString={location.search} queryParametersToParse={{ [TIMELINE_TIMESTAMP_QS]: OptionalString }}>
          {({ parsedQueryString: { timestamp } }) => {
            const mapSliderTimestamp = isValid(new Date(timestamp ?? '')) ? timestamp : undefined;

            return (
              <MapSlider
                timelineBoundaries={{ startTime, endTime }}
                initialTimestamp={mapSliderTimestamp}
                vehicles={vehicles.map((vehicle) => ({
                  history: vehicle.history,
                  breakHistory: vehicle.breakHistory,
                  licensePlateNumber: vehicle.license_plate_number,
                  vehicleId: vehicle.vehicle_id,
                }))}
                tourEvents={tour.events}
                tourStops={getStopsFromTourStops(tour.stops)}
                externalEvents={getExternalEvents(tour)}
                trackingSegments={tour.trackingSegments}>
                {({ selectedPositions }) => (
                  <TourMapView
                    data={{
                      tour,
                      tourSidebarActive: tourSidebarState.tourSidebarActive,
                      ghostVehicles: selectedPositions,
                      vehicles,
                      currentTheme,
                    }}
                  />
                )}
              </MapSlider>
            );
          }}
        </QueryString>
      </ErrorBoundary>
    </div>
  );
};
